import { Link, useRouter, useRouterState } from '@tanstack/react-router'
import { PageTitle } from '@/components/ui'

/**
 * Where an address leads nowhere: a path that is not a page (the router's
 * notFoundComponent), or a task, project, page or list that no longer exists
 * on this device. Like Leave, it says what happened in a sentence and offers
 * a way back, never a bare "Not Found".
 */
const KIND_NOUN = {
  task: 'task',
  project: 'project',
  page: 'page',
  list: 'list',
  course: 'course',
  concept: 'concept',
} as const
export type GoneKind = keyof typeof KIND_NOUN

const btn =
  'flex-1 min-h-11 inline-flex items-center justify-center rounded-[var(--radius-control)] font-medium'

const WayBack = ({ deleted }: { deleted?: boolean }) => {
  const router = useRouter()
  const back = () => {
    if (window.history.length > 1) router.history.back()
    else void router.navigate({ to: '/' })
  }
  return (
    <div className="mt-5 flex gap-3 max-w-sm">
      <button type="button" onClick={back} className={`${btn} bg-raised text-fg`} data-testid="gone-back">
        Back
      </button>
      {deleted ? (
        <Link to="/trash" className={`${btn} bg-accent text-on-accent`} data-testid="gone-trash">
          Open Deleted
        </Link>
      ) : (
        <Link to="/" className={`${btn} bg-accent text-on-accent`} data-testid="gone-home">
          Go to Today
        </Link>
      )}
    </div>
  )
}

/** A path that is not a page. */
export const NotFoundView = () => {
  const path = useRouterState({ select: (s) => s.location.pathname })
  return (
    <div data-testid="not-found">
      <PageTitle>No page here</PageTitle>
      <p className="text-[15px] text-fg-2">
        Nothing in alfieOS lives at this address. The link may be old, or mistyped.
      </p>
      <p className="mt-2 text-xs text-faint break-all">{path}</p>
      <WayBack />
    </div>
  )
}

/** A record's page when the record is not there: deleted (in Deleted), or gone for good. */
export const Gone = ({ kind, deleted }: { kind: GoneKind; deleted?: boolean }) => (
  <div data-testid="gone">
    <PageTitle>This {KIND_NOUN[kind]} is gone</PageTitle>
    <p className="text-[15px] text-fg-2">
      {deleted
        ? `It was deleted. Deleted keeps it for a while, and it can come back from there.`
        : `It was removed for good, or it has not reached this device.`}
    </p>
    <WayBack deleted={deleted} />
  </div>
)
